import { hoursToMinutes } from '@acme/common'
import dayjs from 'dayjs'
import {
    Abono,
    Afastamento,
    CompensacaoDia,
    JornadaDiaria,
    JornadaDiariaQueryXor,
    JornadaMensal,
    JornadaMensalQueryXor,
    Registro,
    jornada,
} from 'service/api'
import { CompensacaoManual } from 'service/api/endpoints/compensacao-manual'
import { FaltaCadastrada } from 'service/api/endpoints/faltas-cadastradas'
import { ViewMode } from '../toggle-view'
import { useDigitacaoContext } from './useDigitacaoContext'

export const invalidateDigitacaoQueries = async () =>
    Promise.all([
        jornada.mensal.invalidateQueries(),
        jornada.diaria.invalidateQueries(),
    ])

type RegistroPayload = (
    action: 'REGISTRO',
    payload: Registro,
    excluir?: boolean,
) => void
type AfastamentoPayload = (
    action: 'AFASTAMENTO',
    payload: Afastamento,
    excluir?: boolean,
) => void
type AbonoPayload = (action: 'ABONO', payload: Abono, excluir?: boolean) => void
type CompensacaoFeriadoPayload = (
    action: 'COMPENSACAO_FERIADO',
    payload: CompensacaoDia,
    excluir?: boolean,
) => void
type FaltaCadastradaPayload = (
    action: 'FALTA_CADASTRADA',
    payload: FaltaCadastrada,
    excluir?: boolean,
) => void
type CompensacaoManualPayload = (
    action: 'COMPENSACAO_MANUAL',
    payload: CompensacaoManual,
    excluir?: boolean,
) => void

type QueryData = RegistroPayload &
    AfastamentoPayload &
    AbonoPayload &
    CompensacaoFeriadoPayload &
    FaltaCadastradaPayload &
    CompensacaoManualPayload

/**
 * Caso o contexto da digitação esteja disponível, retorna uma função que atualiza
 * os dados das rotas abertas
 */
export function useUpdateDigitacaoQueryData() {
    const context = useDigitacaoContext()
    const view = context?.view
    const queryParams = context?.queryParams

    // queryParams vazio = fallback do contexto (provider ausente) — não gravar cache
    if (!view || !queryParams || !Object.keys(queryParams).length) {
        return {
            setQueryData: (_action, _payload, _excluir) => {
                void 0
            },
        } as {
            setQueryData: QueryData
        }
    }

    return useUpdateDigitacaoQueryDataNoContext(view, queryParams)
}

/**
 * Hook que atualiza dados da digitação
 */
export function useUpdateDigitacaoQueryDataNoContext(
    view: ViewMode,
    queryParams: Record<string, any>,
) {
    if (view === 'mensal') {
        const setQueryData: QueryData = (action, payload, excluir) => {
            const applyMensalUpdate = (fn: (oldData?: any[]) => any[]) => {
                jornada.mensal.setQueryData(
                    queryParams as JornadaMensalQueryXor,
                    fn,
                )
            }

            switch (action) {
                case 'REGISTRO':
                    return applyMensalUpdate(
                        updateRegistro(payload as Registro, excluir),
                    )
                case 'AFASTAMENTO':
                    return applyMensalUpdate(
                        updateAfastamento(payload as Afastamento, excluir),
                    )
                case 'ABONO':
                    return applyMensalUpdate(
                        updateAbono(payload as Abono, excluir),
                    )
                case 'COMPENSACAO_FERIADO':
                    return applyMensalUpdate(
                        updateCompensacaoFeriado(
                            payload as CompensacaoDia,
                            excluir,
                        ),
                    )
                case 'FALTA_CADASTRADA':
                    return applyMensalUpdate(
                        updateFaltaCadastrada(
                            payload as FaltaCadastrada,
                            excluir,
                        ),
                    )
                case 'COMPENSACAO_MANUAL':
                    return applyMensalUpdate(
                        updateCompensacaoManual(
                            payload as CompensacaoManual,
                            excluir,
                        ),
                    )
            }
        }

        return { setQueryData }
    } else {
        const setQueryData: QueryData = (action, payload, excluir) => {
            const applyDiarioUpdate = (fn: (oldData?: any[]) => any[]) => {
                jornada.diaria.setQueryData(
                    queryParams as JornadaDiariaQueryXor,
                    fn,
                )
            }

            switch (action) {
                case 'REGISTRO':
                    return applyDiarioUpdate(
                        updateRegistro(payload as Registro, excluir),
                    )
                case 'AFASTAMENTO':
                    return applyDiarioUpdate(
                        updateAfastamento(payload as Afastamento, excluir),
                    )
                case 'ABONO':
                    return applyDiarioUpdate(
                        updateAbono(payload as Abono, excluir),
                    )
                case 'COMPENSACAO_FERIADO':
                    return applyDiarioUpdate(
                        updateCompensacaoFeriado(
                            payload as CompensacaoDia,
                            excluir,
                        ),
                    )
                case 'FALTA_CADASTRADA':
                    return applyDiarioUpdate(
                        updateFaltaCadastrada(
                            payload as FaltaCadastrada,
                            excluir,
                        ),
                    )
                case 'COMPENSACAO_MANUAL':
                    return applyDiarioUpdate(
                        updateCompensacaoManual(
                            payload as CompensacaoManual,
                            excluir,
                        ),
                    )
            }
        }

        return { setQueryData }
    }
}

export function updateRegistro<T extends JornadaDiaria | JornadaMensal>(
    input: Registro | Registro[],
    excluir?: boolean,
) {
    let funcionarioId: number

    return (prev?: T[]): T[] => {
        if (!prev) return [] as T[]

        const oldData = structuredClone(prev)
        if (!Array.isArray(input)) {
            funcionarioId = input.funcionarioId
            input = [input]
        } else funcionarioId = input[0].funcionarioId

        let newData = [...oldData]

        for (const registro of input) {
            registro.updatedAt = new Date()
            const dia = dayjs(registro.data).utc().format('YYYY-MM-DD')

            const indexJornada = newData.findIndex(
                item =>
                    !('id' in item) &&
                    item.registros.some(value => value.id === registro.id),
            )

            newData = newData.map((i, index) => {
                const item = { ...i }
                const diario = 'id' in item

                if (diario) {
                    if (funcionarioId !== item.id) return item

                    const indexRegistro = item.registros.findIndex(
                        value => value.id === registro.id,
                    )

                    const newItem = { ...item, registros: [...item.registros] }

                    if (indexRegistro !== -1) {
                        if (excluir) {
                            newItem.registros.splice(indexRegistro, 1)
                        } else {
                            newItem.registros[indexRegistro] = registro
                        }
                    } else {
                        if (!excluir) {
                            newItem.registros = newItem.registros
                                .concat(registro)
                                .sort((a, b) => {
                                    const horaA = hoursToMinutes(a.hora)
                                    const horaB = hoursToMinutes(b.hora)
                                    return horaA - horaB
                                })
                        }
                    }
                    return newItem as T
                } else {
                    const pertenceALinha =
                        indexJornada !== -1
                            ? index === indexJornada
                            : item.dia === dia

                    if (!pertenceALinha) return item

                    const indexRegistro = item.registros.findIndex(
                        value => value.id === registro.id,
                    )

                    const newItem = { ...item, registros: [...item.registros] }

                    if (indexRegistro !== -1) {
                        if (excluir) {
                            newItem.registros.splice(indexRegistro, 1)
                        } else {
                            newItem.registros[indexRegistro] = registro
                        }
                    } else {
                        if (!excluir) {
                            newItem.registros = newItem.registros
                                .concat(registro)
                                .sort((a, b) => {
                                    const horaA = hoursToMinutes(a.hora)
                                    const horaB = hoursToMinutes(b.hora)
                                    return horaA - horaB
                                })
                        }
                    }
                    return newItem as T
                }
            }) as T[]
        }

        return newData
    }
}

type AfastamentoRange = { dia: string; afastamento: Afastamento }

function updateAfastamento<T extends JornadaDiaria | JornadaMensal>(
    afastamento: Afastamento,
    excluir?: boolean,
) {
    const { funcionarioId, id } = afastamento

    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        const data = structuredClone(oldData)
        const afastamentoRange: AfastamentoRange[] = []

        const diaInicio = dayjs.utc(afastamento.dataInicial)
        const diaFinal = dayjs.utc(afastamento.dataFinal)

        const diff = diaFinal.diff(diaInicio, 'days')

        for (let i = 0; i <= diff; i += 1) {
            const dia = diaInicio.add(i, 'days').format('YYYY-MM-DD')
            afastamentoRange.push({ dia, afastamento })
        }

        return data.map(i => {
            const item = { ...i }
            const diario = 'id' in item

            if (excluir && item.afastamento?.id === id) {
                item.afastamento = undefined
                return item
            }

            const afastamentoExiste = afastamentoRange.find(
                it => it.dia === item.dia,
            )

            if (afastamentoExiste) {
                if (diario && funcionarioId === item.id) {
                    item.afastamento = afastamento
                } else if (
                    !diario &&
                    dayjs
                        .utc(item.dia)
                        .isSameOrBefore(afastamento.dataFinal, 'day') &&
                    dayjs
                        .utc(item.dia)
                        .isSameOrAfter(afastamento.dataInicial, 'day')
                ) {
                    item.afastamento = afastamento
                }
            }

            return item
        })
    }
}

function updateAbono<T extends JornadaDiaria | JornadaMensal>(
    abono: Abono,
    excluir?: boolean,
) {
    const { funcionarioId, id: abonoId } = abono

    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        console.debug('updateAbono', abono, excluir)

        const data = structuredClone(oldData)
        return data.map(i => {
            const item = { ...i }
            // Caso o abono tenha alterado o dia
            if (item.abono && item.abono.id === abonoId) {
                item.abono = undefined
            }

            const diario = 'id' in item

            if (diario && funcionarioId === item.id) {
                if (excluir) item.abono = undefined
                else item.abono = abono
            } else if (
                !diario &&
                dayjs(item.dia).utc().isSame(abono.data, 'day')
            ) {
                if (excluir) item.abono = undefined
                else item.abono = abono
            }

            return item
        })
    }
}

function updateFaltaCadastrada<T extends JornadaDiaria | JornadaMensal>(
    falta: FaltaCadastrada,
    excluir?: boolean,
) {
    const { funcionarioId } = falta

    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        const data = structuredClone(oldData)
        return data.map(i => {
            const item = { ...i }
            const diario = 'id' in item

            if (diario && funcionarioId === item.id) {
                if (excluir) item.faltaCadastrada = undefined
                else item.faltaCadastrada = falta
            } else if (
                !diario &&
                dayjs(item.dia).utc().isSame(falta.data, 'day')
            ) {
                if (excluir) item.faltaCadastrada = undefined
                else item.faltaCadastrada = falta
            }

            return item
        })
    }
}

function updateCompensacaoFeriado<T extends JornadaDiaria | JornadaMensal>(
    compensacaoFeriado: CompensacaoDia,
    excluir?: boolean,
) {
    const { funcionarioId } = compensacaoFeriado

    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        const data = structuredClone(oldData)
        return data.map(i => {
            const item = { ...i }
            const diario = 'id' in item

            if (diario && funcionarioId === item.id) {
                if (excluir) item.compensacao = undefined
                else item.compensacao = compensacaoFeriado
            } else if (
                !diario &&
                dayjs(item.dia)
                    .utc()
                    .isSame(compensacaoFeriado.dataCompensacao, 'day')
            ) {
                if (excluir) item.compensacao = undefined
                else item.compensacao = compensacaoFeriado
            }

            return item
        })
    }
}

function updateCompensacaoManual<T extends JornadaDiaria | JornadaMensal>(
    compensacaoManual: CompensacaoManual,
    excluir?: boolean,
) {
    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        const data = structuredClone(oldData)
        return data.map(i => {
            const item = { ...i }
            // Verificar se a data principal corresponde ao dia atual
            const isOriginDay = dayjs(item.dia)
                .utc()
                .isSame(compensacaoManual.data, 'day')

            // Verificar se algum destino corresponde ao dia atual
            const isDestinyDay = compensacaoManual.Destinos?.some(destino =>
                dayjs(item.dia).utc().isSame(destino.data, 'day'),
            )

            if (isOriginDay || isDestinyDay) {
                if (excluir) item.compensacaoManual = undefined
                else item.compensacaoManual = compensacaoManual
            }

            return item
        })
    }
}
